import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Crown } from "lucide-react";
import AnySkillLogo from "./AnySkillLogo";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import NavbarSidebar from "./NavbarSidebar";
import NotificationBell from "./NotificationBell";
import { UserAvatar } from "@/components/UserAvatar";

const Navbar = () => {
  const { user, profile } = useAuth();
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/", {
      state: {
        fromNavbar: true
      }
    });
  };
  
  return (
    <nav className={`border-b bg-background border-border ${isAdmin ? 'border-red-500 border-b-2' : ''}`}>
      {/* Admin Mode Badge - Only visible to admins with fixed height */}
      <div className={`text-white text-center text-sm font-medium transition-all duration-200 ${isAdmin ? 'bg-red-500 py-1 h-8' : 'h-0 overflow-hidden'}`}>
        {isAdmin && <>
            <Crown className="inline-block w-4 h-4 mr-2" />
            ADMIN MODE
          </>}
      </div>
      
      <div className="py-4">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center">
            <button onClick={handleLogoClick} className="flex items-center font-poppins font-bold text-2xl text-anyskill-purple hover:opacity-80 transition-opacity gap-px">
              <AnySkillLogo size={32} />
              Any<span className="text-anyskill-blue">Skill</span>
              {isAdmin && <Crown className="inline-block w-5 h-5 ml-2 text-red-500" />}
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {user ? <>
                {/* Notifications Bell */}
                <NotificationBell />
                
                {/* My Profile Avatar */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/my-profile">
                      <div className="relative">
                        <UserAvatar
                          userId={user?.id}
                          avatarUrl={profile?.avatar_url}
                          fallback={
                            profile?.first_name && profile?.last_name 
                              ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
                              : user?.email?.[0].toUpperCase() || 'U'
                          }
                          size="sm"
                          className="border-2 border-background shadow-lg hover:scale-105 transition-transform cursor-pointer"
                          showLevel={true}
                        />
                        {isAdmin && (
                          <Crown className="w-3 h-3 text-red-500 absolute -top-1 -right-1" />
                        )}
                      </div>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>My Profile{isAdmin ? ' (Admin)' : ''}</p>
                  </TooltipContent>
                </Tooltip>
                
                {/* Hamburger Menu with Sidebar - moved to far right */}
                <NavbarSidebar isAdmin={isAdmin} />
              </> : <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/auth?mode=login">
                      <Button variant="outline" size="icon" className="font-inter" aria-label="Log In">
                        <LogIn size={16} />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Log In</p>
                  </TooltipContent>
                </Tooltip>
                
                <Link to="/auth?mode=signup">
                  <Button className="bg-anyskill-purple hover:bg-anyskill-purple/90 font-inter">
                    Sign Up
                  </Button>
                </Link>
              </>}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;